import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCopy, FaCheck } from 'react-icons/fa';
import SEO from '../components/SEO';

const DonationContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1));
    transition: opacity 0.3s ease;
    z-index: -1;

    @media (max-width: 768px) {
      background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.2));
      background-attachment: scroll;
    }
  }
  overflow-x: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, ${props => props.theme.colors.background}, transparent);
    z-index: 2;
  }
`;

const PageHero = styled.section`
  min-height: 60vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 20px 160px;
  position: relative;
  
  @media (max-width: 768px) {
    min-height: 50vh;
    padding: 80px 20px 120px;
  }



`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageHeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  animation: ${fadeUp} 1s ease-out;
  
`;

const PageTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  line-height: 1.2;
  
  span {
    color: ${props => props.theme.colors.primary};
    display: inline-block;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -5px;
      width: 100%;
      height: 3px;
      background: ${props => props.theme.colors.primary};
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.3s ease;
    }
  }
  
  &:hover span::after {
    transform: scaleX(1);
    transform-origin: left;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: clamp(1rem, 2vw, 1.2rem);
  line-height: 1.8;
  opacity: 0.9;
  margin-bottom: 2rem;
  max-width: 800px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;


const CardContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 20px;
  margin-top: -80px;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 15px;
    margin-top: -40px;
  }
`;

const Card = styled.div`
  margin: 0 0 20vh;
  background: ${({ theme }) => theme.colors.light};
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  padding: 40px;
  max-width: 500px;
  width: 100%;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease-in-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, 
      ${({ theme }) => theme.colors.primary}, 
      ${({ theme }) => theme.colors.secondary || theme.colors.primary + '80'}
    );
  }

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: 16px;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }
`;

const BankInfo = styled.div`
  margin: 32px 0;
  position: relative;
  
  &:first-child {
    margin-top: 0;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, 
      rgba(0, 0, 0, 0.05), 
      rgba(0, 0, 0, 0.1), 
      rgba(0, 0, 0, 0.05)
    );
  }

  @media (max-width: 768px) {
    margin: 24px 0;
    
    &:not(:last-child)::after {
      bottom: -12px;
    }
  }
`;

const Label = styled.div`
  color: ${({ theme }) => theme.colors.gray};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  margin-bottom: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    width: 4px;
    height: 4px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 8px;
  }
`;

const ValueContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Value = styled.div`
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: 500;
  font-family: 'Roboto Mono', monospace;
  background: rgba(245, 245, 245, 0.9);
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(224, 224, 224, 0.8);
  text-align: center;
  letter-spacing: 0.5px;
  user-select: all;
  flex-grow: 1;
  transition: all 0.2s ease;
  word-break: break-all;

  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSizes.medium};
    padding: 12px;
  }

  &:hover {
    background: rgba(255, 255, 255, 1);
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all 0.2s ease;
  border-radius: 8px;

  &:hover {
    transform: scale(1.1);
    background: ${({ theme }) => `${theme.colors.primary}10`};
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 6px;
  }
`;

const CopyFeedback = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 0.9rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: ${fadeUp} 0.3s ease-out;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '✓';
    font-weight: bold;
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.85rem;
    bottom: 16px;
  }
`;




const DonationPage = () => {
  const [copiedValue, setCopiedValue] = useState('');

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedValue(text);
      setTimeout(() => setCopiedValue(''), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <DonationContainer>
    <SEO
      title="Donate | Support The Big Heart Association Morocco"
      description="Make a donation to The Big Heart Association (Association Grand Coeur) via La Banque Populaire. Every contribution helps us bring education, healthcare, and hope to rural Morocco."
      url="/donate"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Donate", url: "/donate" }]}
      noOrganizationSchema
    />
    <PageHero>
    <PageHeroContent>
      <PageTitle>Make a <span>Donation</span></PageTitle>
      <PageSubtitle> Your generous donation helps us continue our mission. All contributions, no matter the size,
      make a significant impact in our community.</PageSubtitle>
    </PageHeroContent>
  </PageHero>
    
      
      
      <CardContainer>
        <Card>
          <BankInfo>
            <Label>Bank Name</Label>
            <ValueContainer>
              <Value>La Banque Populaire</Value>
            </ValueContainer>
          </BankInfo>
          
          <BankInfo>
            <Label>Account Name</Label>
            <ValueContainer>
              <Value>Association Grand Coeur</Value>
              <CopyButton onClick={() => handleCopy('Association Grand Coeur')}>
                {copiedValue === 'Association Grand Coeur' ? <FaCheck /> : <FaCopy />}
              </CopyButton>
            </ValueContainer>
          </BankInfo>
          
          <BankInfo>
            <Label>RIB</Label>
            <ValueContainer>
              <Value>190 780 21211 69386710001 68</Value>
              <CopyButton onClick={() => handleCopy('190 780 21211 69386710001 68')}>
                {copiedValue === '190 780 21211 69386710001 68' ? <FaCheck /> : <FaCopy />}
              </CopyButton>
            </ValueContainer>
            {copiedValue && (
              <CopyFeedback>
                Copied to clipboard!
              </CopyFeedback>
            )}
          </BankInfo>
        </Card>
      </CardContainer>
    </DonationContainer>
  );
};

export default DonationPage;
